<template>
    <div class="contact_editor">
        <div class="md-layout md-gutter margin_bottom">
            <label class="md-layout-item md-size-25 md-form-label">
           {{label}} 
            </label>
            <div class="md-layout-item md-size-40">
                <ValidationProvider :name="label" :rules="{ required:true, numeric : true, regex: /[1-9]*/ }" v-slot="{ passed, failed }" tag="div" class="val_with_extras">
                    <md-field class="offset_val">
                        <label>{{selectLabel}}</label>
                        <md-select v-model="contact.id" :disabled="selectContactDisabled">
                            <md-option>Add new...</md-option>
                            <md-option v-for="c in contacts" :key="c.id" :value="c.id">{{c.name}}</md-option>
                        </md-select>
                        <slide-y-down-transition><md-icon class="error" v-show="failed">close</md-icon></slide-y-down-transition>
                        <slide-y-down-transition><md-icon class="success" v-show="passed">done</md-icon></slide-y-down-transition>
                    </md-field>
                    <md-button class="md-icon-button md-raised md-dense btn_select_action" v-if="showEdit" @click="edit" :disabled="selectContactDisabled" title="Edit selected contact">
                        <md-icon>edit</md-icon>
                    </md-button>
                    <md-button class="md-icon-button md-raised md-dense btn_select_action btn_select_bold" v-if="canAdd" @click="add" :disabled="selectContactDisabled" title="Add new contact">
                        <md-icon>add</md-icon>
                    </md-button>
                </ValidationProvider>
            </div>
        </div>

        <modal v-if="detailsVisible" @close="modalHide">
            <template slot="header">
                  <div class="md-layout-item md-medium-size-100 md-xlarge-size-75">
                    <h2 class="dialog_title">{{dialogTitle}}</h2>
                    <p>{{dialogSubtitle}}</p>
                </div>
                <md-button class="md-simple md-just-icon md-round modal-default-button" @click="modalHide">
                    <md-icon>clear</md-icon>
                </md-button>
            </template>
            <template slot="body">
                <ValidationObserver ref="valObserverContactInner" :disabled="!detailsVisible">
                    <contact-inner-details :contact="contact" :controlsClass="'md-size-70'">
                    </contact-inner-details>
                </ValidationObserver>
            </template>
            <template slot="footer">
                <div class="md-layout md-alignment-space-between margin_top">
                    <div class="md-layout-item md-alignment-top-right">
                        <md-button @click="modalHide" class="md-button md-danger btn-fill">Cancel</md-button>
                    </div>
                    <div class="md-layout-item md-alignment-top-right md-layout">
                        <md-button @click="save" class="md-button md-success btn-fill">Save</md-button>
                    </div>
                </div>
            </template>
        </modal>

    </div>
</template>
<script>
    import { SlideYDownTransition } from "vue2-transitions";
    import { extend } from "vee-validate";
    import { required, min, max, email } from "vee-validate/dist/rules";
    extend("required", required);
    extend("min", min);
    extend("max", max);
    extend("email", email);

    import Help from "@/ts/Help";
    import NotyTypes from "@/ts/NotyTypes";
    import Api from '@/ts/UvApiService';
    import { ControlStates } from '@/ts/ContactComponents';
    import ContactInnerDetails from '@/components/Contact/InnerDetails.vue';

    import { Modal } from '@/components/';

    export default {
        name: "ContactEditor",
        components: {
            SlideYDownTransition
            , ContactInnerDetails
            , Modal
        },
        props: {
            disabledLabel: {
                type: String,
                default: () => "Please wait..."
            },
            isDisabled: {
                type: Boolean,
                default: () => false
            },
            hide: {
                type: Boolean,
                default: () => false
            },
            label: null,
            contactId: null,
            contacts: {
                type: Array,
                default: () => []
            },
            isStaff: null,
            facilitiesManagers: {
                type: Array,
                default: () => []
            },
            portfolios: {
                type: Array,
                default: () => []
            },
        },
        data() {
            return {
                controlState: ControlStates.notSelected,
                contact: {
                    id: this.contactId,
                    name: null,
                    email: null,
                    telephone: null,

                    isStaff: null,
                    facilitiesManagers: [],
                    portfolios: [],
                },
            }
        },
        computed: {
            controlStateString() {
                return ControlStates[this.controlState];
            },
            selectContactDisabled() {
                return this.hide || this.isDisabled;
            },
            selectLabel() {
                if (this.selectContactDisabled)
                    return this.disabledLabel;
                if (this.controlState === ControlStates.editing)
                    return "Edit Contact";
                if (this.controlState === ControlStates.adding)
                    return "Add New Contact";
                if (this.controlState === ControlStates.busy)
                    return "Please Wait...";
                return "Choose Contact";
            },
            detailsVisible() {
                return Help.contains([ControlStates.editing, ControlStates.adding], this.controlState);
            },
            showEdit() {
                return this.canSave || this.canEdit;
            },
            canSave() {
                return this.detailsVisible;
            },
            canEdit() {
                return !this.detailsVisible && (this.existingContactSelected);
            },
            canAdd() {
                return !this.detailsVisible && (this.contact.id === false || Help.isNullOrUndef(this.contact.id)); //nothing selected
            },
            contactSelected() {
                return !Help.isNullOrUndef(this.contact.id) && Help.isGoodNumber(this.contact.id);
            },
            dialogTitle() {
                return this.controlState === ControlStates.editing ? "Edit Contact" : "Add Contact";
            },
            dialogSubtitle() {
                if (this.controlState === ControlStates.editing)
                    return this.isStaff ? "Edit in-house contact" : "Edit external organisation contact";
                else
                    return this.isStaff ? "To in-house team" : "To external organisation";
            },
            existingContactSelected() {
                return !Help.isNullOrUndef(this.contact.id) && Help.isGoodNumber(this.contact.id) && this.contact.id > 0;
            },
        },
        methods: {
            notifyErrors(errorsArray) {
                var errorsLis = "";

                errorsArray.forEach((e) => {
                    if (e.length > 0) {
                        e.flat().forEach((ei) => {
                            errorsLis += `<li>${Help.removeFullStop(ei)}</li>`;
                        });
                    }
                });

                this.$notify({
                    timeout: 3500,
                    message: `Please <b>fix all errors</b> in contact details <b>to save</b><ul>${errorsLis}</ul>`,
                    icon: "add_alert",
                    horizontalAlign: 'center',
                    verticalAlign: 'top',
                    type: NotyTypes[NotyTypes.danger]
                });
            },
            async modalHide() {
                if (this.controlState === ControlStates.adding) {
                    this.contact.id = false;
                }
                else if (this.controlState === ControlStates.editing) {
                    this.controlState = ControlStates.selected;
                }
            },
            async save() {
                this.$refs.valObserverContactInner.validate().then(async success => {
                    if (!success) {
                        setTimeout(() => { //TODO: update vee validate timeout here is to ensure errors are there and correct https://github.com/logaretm/vee-validate/issues/2248#issuecomment-578229806
                            this.notifyErrors(Object.values(this.$refs.valObserverContactInner.errors));
                        }, 100);
                        return;
                    }
                    else {
                        this.controlState = ControlStates.busy;
                        this.contact = await Api.contacts.upsertContact(this.contact); 
                        this.controlState = ControlStates.selected;
                        this.$emit('refreshList');
                    }
                });
            },
            async edit() {
                this.controlState = ControlStates.busy;
                this.contact = await Api.contacts.getContact(this.contact.id);
                this.controlState = ControlStates.editing;
            },
            async add() {
                this.contact = {
                    id: 0,
                    name: null,
                    email: null,
                    telephone: null,
                    isStaff: this.isStaff,
                    facilitiesManagers: this.facilitiesManagers,
                    portfolios: this.portfolios,
                };
                this.controlState = ControlStates.adding;
            },
        },
        watch: {
            async 'contact.id'(v) {
                this.$emit('changeId', this.contact.id);
                if (this.contactSelected) {
                    if (this.contact.id > 0) {
                        this.controlState = ControlStates.selected;
                    }
                    else {
                        //console.log("this is the issue");
                        //console.log(this.contact.id);
                        //should probably set this here 
                        //this.controlState = ControlStates.notSelected;
                    }
                }
                else if (this.contact.id !== 0) {
                    this.controlState = ControlStates.notSelected;
                }
            },
            contactId(v) {
                this.contact.id = this.contactId;
            }
        },
        async mounted() {
            if (this.existingContactSelected)
                this.controlState = ControlStates.selected;
        }
    };
</script>
<style lang="scss">

    .no_contacts {
        margin-top: 1.1rem;
    }

</style>